import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { CheckCircle, ArrowRight, CreditCard } from 'lucide-react';

const PaymentSuccess = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isProActivated = searchParams.get('pro') === 'activated';
  
  const [status, setStatus] = useState('success');
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if this is a valid pro activation
    if (!isProActivated) {
      navigate('/dashboard');
      return;
    }

    // Verify user is actually upgraded
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.plan_status !== 'active_pro') {
      setError('Pro activation verification failed');
      setStatus('error');
    }
  }, [isProActivated, navigate]);

  const handleContinue = () => {
    navigate('/dashboard?welcome=pro');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: theme.mainPanelBg }}
    >
      <div className="max-w-md w-full text-center">
        
        {status === 'success' && (
          <>
            <CheckCircle size={64} style={{ color: '#10b981' }} className="mx-auto mb-6" />
            <h1 
              className="text-3xl font-bold mb-4"
              style={{ color: theme.mainPanelText }}
            >
              Welcome to Pro!
            </h1>
            <div 
              className="rounded-lg p-6 mb-6"
              style={{ backgroundColor: theme.sidebarBg }}
            >
              <p 
                className="text-lg mb-4"
                style={{ color: theme.mainPanelText }}
              >
                Your pro subscription is now active and ready to use.
              </p>
              <div className="text-sm opacity-80 space-y-2" style={{ color: theme.mainPanelText }}>
                <div className="flex items-center justify-center space-x-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span>Pro subscription activated</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span>500,000 AI tokens added</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span>All pro features unlocked</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <CheckCircle size={16} className="text-green-500" />
                  <span>Priority support enabled</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleContinue}
              className="flex items-center justify-center mx-auto px-8 py-4 rounded-lg font-semibold transition-all hover:opacity-90"
              style={{
                backgroundColor: theme.buttonBg,
                color: theme.buttonText
              }}
            >
              Continue to Dashboard
              <ArrowRight size={20} className="ml-2" />
            </button>
            
            <div 
              className="mt-6 p-4 rounded-lg text-sm"
              style={{ backgroundColor: theme.sidebarBg, color: theme.mainPanelText }}
            >
              <div className="flex items-center justify-center space-x-2 mb-2">
                <CreditCard size={16} className="opacity-60" />
                <span className="font-medium">Billing Information</span>
              </div>
              <p className="opacity-80">
                Your subscription is active and billing has begun. 
                You can manage your subscription and billing in your account settings.
              </p>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: '#ef4444' }}
            >
              <span className="text-white text-2xl">!</span>
            </div>
            <h1 
              className="text-2xl font-bold mb-4"
              style={{ color: theme.mainPanelText }}
            >
              Pro Activation Issue
            </h1>
            <div 
              className="rounded-lg p-4 mb-6 bg-red-50 border-l-4 border-red-400"
            >
              <p className="text-red-700 text-sm">
                {error || 'There was an issue verifying your pro subscription.'}
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/upgrade')}
                className="w-full py-3 px-4 rounded-lg font-medium"
                style={{
                  backgroundColor: theme.buttonBg,
                  color: theme.buttonText
                }}
              >
                Try Upgrading Again
              </button>
              
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 px-4 rounded-lg font-medium border"
                style={{
                  borderColor: theme.inputBorder,
                  color: theme.mainPanelText,
                  backgroundColor: 'transparent'
                }}
              >
                Go to Dashboard
              </button>
            </div>
            
            <p className="text-xs mt-4 opacity-60" style={{ color: theme.mainPanelText }}>
              If you continue to experience issues, please contact support.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;